import { Component, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IkigaiService } from "../../../../core/services/ikigai.service";
import {
  IkigaiData,
  IkigaiQuestion,
  IkigaiAnswersDto,
} from "../../../../core/models/ikigai.interface";
import { Observable } from "rxjs";
import jsPDF from "jspdf";
import { LoaderService } from "../../../../core/shared/services/loader.service";

@Component({
  selector: "app-ikigai",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./ikigai.component.html",
  styleUrls: ["./ikigai.component.scss"],
})
export class IkigaiComponent implements OnInit {
  private loader= inject(LoaderService);
  // Estado actual
  currentStep: "introduction" | "ikigai" | "passion" | "mission" | "vocation" | "profession" = "introduction";
  stepComplete: { [key: string]: boolean } = {
    passion: false,
    mission: false,
    vocation: false,
    profession: false,
    ikigai: false
  };

  // Respuestas del usuario
  userAnswer: string = "";
  ikigaiData: IkigaiData;

  // Configuraciones de preguntas para cada etapa
  questions: { [key: string]: IkigaiQuestion } = {
    passion: {
      title: "Pasión",
      subtitle: "Las habilidades que más domino y que amo hacer.",
      question: "¿En qué actividades soy experto y amo dedicarme a ellas?",
      maxLength: 255,
      step: 1,
      totalSteps: 5,
    },
    mission: {
      title: "Misión",
      subtitle: "Lo que el mundo necesita y yo amo hacer.",
      question:
        "¿Qué requiere el mundo y está relacionado con una actividad que amo?",
      maxLength: 255,
      step: 2,
      totalSteps: 5,
    },
    vocation: {
      title: "Vocación",
      subtitle:
        "Lo que el mundo necesita y es algo por lo que me pagan o me podrían pagar.",
      question:
        "¿Qué requiere el mundo y está relacionado con una actividad por la que hoy me pagan o por la que me podrían pagar?",
      maxLength: 255,
      step: 3,
      totalSteps: 5,
    },
    profession: {
      title: "Profesión",
      subtitle:
        "Las habilidades que más domino y son pagadas, así como en aquello que deseo volverme experto y además me paguen.",
      question:
        "¿En qué actividades soy experto y me pagan? ¿En qué me puedo volver experto y me podrían pagar?",
      maxLength: 255,
      step: 4,
      totalSteps: 5,
    },
    ikigai: {
      title: "Ikigai",
      subtitle:
        "Mi razón de ser; donde pasión, talento, necesidad global y sustento se unen.",
      question:
        "¿Qué actividades me hacen perder la noción del tiempo y me llenan de energía?¿Cómo puedo combinar lo que me apasiona con lo que el mundo necesita y por lo que me pagarían?",
      maxLength: 255,
      step: 5,
      totalSteps: 5,
    },
  };

  constructor(private ikigaiService: IkigaiService, private router: Router) {
    this.ikigaiData = {
      passion: { answer: "", completed: false },
      mission: { answer: "", completed: false },
      vocation: { answer: "", completed: false },
      profession: { answer: "", completed: false },
      ikigai: { answer: "", completed: false },
    };
  }

  ngOnInit(): void {
    this.loader.show();
    this.ikigaiService.getIkigaiData().subscribe((data) => {
      if (data) {
        this.ikigaiData = data;

        // Actualizar el estado de completitud de cada paso
        this.stepComplete = {
          passion: data.passion.completed,
          mission: data.mission.completed,
          vocation: data.vocation.completed,
          profession: data.profession.completed,
          ikigai: data.ikigai.completed,
        };

        if (this.allStepsCompleted()) {
          this.currentStep = "ikigai";
        } else {
          // Podríamos navegar al primer paso no completado, o simplemente dejar que el usuario
          // haga clic en "Empezar aquí" para ir a pasión, o si ya empezó, que los tabs lo lleven.
          // Por ahora, la intro se mostrará siempre a menos que todo esté completo.
        }
      }
      // Cargar respuesta actual si no estamos en introduction o ikigai
      if (
        this.currentStep !== "introduction" 
      ) {
        this.loadCurrentAnswer();
      }
      this.loader.hide();
    });
  }

  loadCurrentAnswer(): void {
    if (
      this.currentStep === "introduction"
    )
      return;

    switch (this.currentStep) {
      case "passion":
        this.userAnswer = this.ikigaiData.passion.answer;
        break;
      case "mission":
        this.userAnswer = this.ikigaiData.mission.answer;
        break;
      case "vocation":
        this.userAnswer = this.ikigaiData.vocation.answer;
        break;
      case "profession":
        this.userAnswer = this.ikigaiData.profession.answer;
        break;
      case "ikigai":
        this.userAnswer = this.ikigaiData.ikigai.answer;
        break;
    }
  }

  navigateTo(
    step:
      | "introduction"
      | "passion"
      | "mission"
      | "vocation"
      | "profession"
      | "ikigai"
  ): void {
    // Verificar si el tab está habilitado antes de navegar
    if (this.isTabDisabled(step)) {
      // Si se intenta navegar a un tab deshabilitado mediante la URL o algún otro método no previsto,
      // podríamos optar por no hacer nada, o redirigir al paso actual o a la intro.
      // Por ahora, si el click en el tab está prevenido por [disabled], esto es más para lógica interna.
      return;
    }

    if (this.currentStep !== "introduction") {
      this.saveCurrentAnswer(); // Guardar solo si estábamos en una vista de preguntas
    }

    this.currentStep = step;

    if (this.currentStep !== "introduction" ) {
      this.loadCurrentAnswer(); // Cargar solo si vamos a una vista de preguntas
    }

    // if (step === "ikigai" && this.allStepsCompleted()) {
    //   this.ikigaiService.generateSummary();
    // }
  }

  saveCurrentAnswer(): void {
    if (this.currentStep === "introduction")
      return;

    let serviceCall: Observable<IkigaiAnswersDto>;

    switch (this.currentStep) {
      case "passion":
        serviceCall = this.ikigaiService.updatePassion(this.userAnswer);
        break;
      case "mission":
        serviceCall = this.ikigaiService.updateMission(this.userAnswer);
        break;
      case "vocation":
        serviceCall = this.ikigaiService.updateVocation(this.userAnswer);
        break;
      case "profession":
        serviceCall = this.ikigaiService.updateProfession(this.userAnswer);
        break;
      case "ikigai":
        serviceCall = this.ikigaiService.updateIkigai(this.userAnswer);
        break;
      default:
        return; // No debería llegar aquí
    }

    serviceCall.subscribe({
      next: (updatedAnswers) => {
        // Actualizar también los datos locales del componente
        this.ikigaiData[this.currentStep as keyof IkigaiData].answer =
          this.userAnswer;
        this.ikigaiData[this.currentStep as keyof IkigaiData].completed =
          !!this.userAnswer.trim();
      },
      error: (err) => {
        console.error(`Error updating ${this.currentStep} on backend:`, err);
        // Aún así actualizar localmente en caso de error de red
        this.ikigaiData[this.currentStep as keyof IkigaiData].answer =
          this.userAnswer;
        this.ikigaiData[this.currentStep as keyof IkigaiData].completed =
          !!this.userAnswer.trim();
      },
    });
  }

  nextStep(): void {
    if (this.currentStep === "introduction")
      return;

    this.saveCurrentAnswer();
    this.stepComplete[this.currentStep] = true; // Marcar el paso actual como completo
    this.ikigaiData[this.currentStep].completed = true; // Asegurar que se refleja en ikigaiData para el servicio
    if(this.currentStep !== "ikigai") {
      this.userAnswer = ""; // Limpiar para el siguiente paso
    }
    
    const stepsOrder: Array<
      "passion" | "mission" | "vocation" | "profession" | "ikigai"
    > = ["passion", "mission", "vocation", "profession", "ikigai"];
    const currentIndex = stepsOrder.indexOf(this.currentStep as any); // Type assertion as currentStep here cannot be 'introduction'


    if (currentIndex !== -1 && currentIndex < stepsOrder.length - 1) {
      this.currentStep = stepsOrder[currentIndex + 1];
      // if (this.currentStep !== "ikigai") {
      this.loadCurrentAnswer();
      // } else {
      //   // Si el siguiente paso es 'ikigai', se asume que todas las preguntas se completaron.
      //   this.ikigaiService.generateSummary();
      // }
    } else if (this.currentStep === "profession") {
      // Caso especial si estamos en profession y es el último de preguntas
      // this.currentStep = "ikigai";
      // this.ikigaiService.generateSummary();
    }
  }

  allStepsCompleted(): boolean {
    return (
      this.stepComplete["passion"] &&
      this.stepComplete["mission"] &&
      this.stepComplete["vocation"] &&
      this.stepComplete["profession"] &&
      this.stepComplete["ikigai"]
    );
  }

  startIkigaiProcess(): void {
    this.currentStep = "passion";
    this.loadCurrentAnswer();
  }

  addObjectives(): void {
    // Navegación hacia la sección de objetivos
    this.router.navigate(["/dashboard/objectives"]);
  }

  downloadIkigai(): void {
    // Primero guardar la respuesta actual si estamos en una vista de preguntas
    if (
      this.currentStep !== "introduction" &&
      this.currentStep !== "ikigai" &&
      this.userAnswer.trim()
    ) {
      this.saveCurrentAnswer();
      // Esperar un poco para que se complete el guardado
      setTimeout(() => {
        this.getDataAndGeneratePDF();
      }, 100);
    } else {
      this.getDataAndGeneratePDF();
    }
  }

  private getDataAndGeneratePDF(): void {
    // Obtener datos locales primero
    let localData = this.ikigaiService.getLocalIkigaiData();

    // Si estamos en una vista de preguntas y hay una respuesta actual, incluirla
    if (
      this.currentStep !== "introduction" &&
      this.currentStep !== "ikigai" &&
      this.userAnswer.trim()
    ) {
      localData[this.currentStep as keyof IkigaiData].answer = this.userAnswer;
    }

    // También usar los datos del componente como fallback
    if (!localData.passion.answer && this.ikigaiData.passion.answer) {
      localData.passion.answer = this.ikigaiData.passion.answer;
    }
    if (!localData.mission.answer && this.ikigaiData.mission.answer) {
      localData.mission.answer = this.ikigaiData.mission.answer;
    }
    if (!localData.vocation.answer && this.ikigaiData.vocation.answer) {
      localData.vocation.answer = this.ikigaiData.vocation.answer;
    }
    if (!localData.profession.answer && this.ikigaiData.profession.answer) {
      localData.profession.answer = this.ikigaiData.profession.answer;
    }

    this.generatePDFWithData(localData);
  }

  private generatePDFWithData(ikigaiData: IkigaiData): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = 30;

    // Función auxiliar para agregar texto con salto de línea automático
    const addTextWithWrap = (
      text: string,
      x: number,
      y: number,
      maxWidth: number,
      fontSize: number = 12
    ): number => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + lines.length * (fontSize * 0.6);
    };

    // Función auxiliar para verificar si necesitamos nueva página
    const checkNewPage = (requiredSpace: number): number => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        return 30; // Reset Y position for new page
      }
      return yPosition;
    };

    // Título principal
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Resultados de la herramienta", margin, yPosition);
    yPosition += 15;

    // Subtítulo descriptivo
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const subtitleText =
      "Este documento contiene los resultados de tu análisis Ikigai, una herramienta japonesa que te ayuda a encontrar tu propósito de vida a través de la intersección entre lo que amas, lo que sabes hacer bien, lo que el mundo necesita y por lo que te pueden pagar.";
    yPosition = addTextWithWrap(subtitleText, margin, yPosition, maxWidth, 12);
    yPosition += 10;

    // Secciones del Ikigai
    const sections = [
      {
        key: "passion",
        data: ikigaiData.passion,
        config: this.questions["passion"],
      },
      {
        key: "mission",
        data: ikigaiData.mission,
        config: this.questions["mission"],
      },
      {
        key: "vocation",
        data: ikigaiData.vocation,
        config: this.questions["vocation"],
      },
      {
        key: "profession",
        data: ikigaiData.profession,
        config: this.questions["profession"],
      },
      {
        key: "ikigai",
        data: ikigaiData.ikigai,
        config: this.questions["ikigai"],
      },
    ];

    sections.forEach((section) => {
      // Verificar si necesitamos nueva página
      yPosition = checkNewPage(60);

      // Título de la sección
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`Mi ${section.config.title}`, margin, yPosition);
      yPosition += 12;

      // Subtítulo/Pregunta de la sección
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      yPosition = addTextWithWrap(
        section.config.subtitle,
        margin,
        yPosition,
        maxWidth,
        11
      );
      yPosition += 8;

      // Respuesta del usuario
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const answer = section.data.answer?.trim() || "Sin respuesta registrada";
      yPosition = addTextWithWrap(answer, margin, yPosition, maxWidth, 10);
      yPosition += 8;
    });

    // Sección final "Mi Ikigai"
    // yPosition = checkNewPage(40);
    // yPosition += 10;

    // doc.setFontSize(18);
    // doc.setFont("helvetica", "bold");
    // doc.text("Mi Ikigai", margin, yPosition);
    // yPosition += 15;

    // doc.setFontSize(12);
    // doc.setFont("helvetica", "normal");
    // const ikigaiText =
    //   "Tu Ikigai se encuentra en la intersección de estos cuatro elementos. Es aquello que amas hacer, en lo que eres bueno, lo que el mundo necesita y por lo que te pueden pagar. Reflexiona sobre las conexiones entre tus respuestas para descubrir tu propósito único.";
    // addTextWithWrap(ikigaiText, margin, yPosition, maxWidth, 12);

    // Descargar el PDF
    const fileName = `mi-ikigai-${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  }

  isTabDisabled(
    tabName:
      | "introduction"
      | "passion"
      | "mission"
      | "vocation"
      | "profession"
      | "ikigai"
  ): boolean {
    if (tabName === "introduction") return true; // No es un tab navegable

    if (this.currentStep === "introduction") {
      return true; // Todos los tabs deshabilitados durante la introducción
    }

    // Una vez iniciadas las preguntas
    switch (tabName) {
      case "passion":
        return false; // Pasión siempre es accesible una vez iniciadas las preguntas
      case "mission":
        return !this.stepComplete["passion"];
      case "vocation":
        return !this.stepComplete["mission"];
      case "profession":
        return !this.stepComplete["vocation"];
      case "ikigai":
        return !this.stepComplete["profession"];
      default:
        return true;
    }
  }
}
